import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Input, Tooltip } from 'antd';
import { FC } from 'react';
import * as THREE from 'three';
import { fixtureByIdMap } from '../../../../../StoreBuilderDetailsView/figures';

import { formatCategoryName } from '../../utils';
import './FixtureProperties.less';

interface FixturePropertiesProp {
  showProperties: boolean;
  toggleShowProperties: () => void;
  selectedMeshes: THREE.Mesh[];
  cubeSize: { width: number; height: number; depth: number };
  sphereRadius: number;
  handleUpdateCubeSize: (
    mesh: THREE.Mesh,
    field: 'width' | 'height' | 'depth',
    value: string
  ) => void;
  handleUpdateSphereRadius: (mesh: THREE.Mesh, value: string) => void;
  handleUpdateColor?: (mesh: THREE.Mesh, color: string) => void;
}

const FixtureProperties: FC<FixturePropertiesProp> = ({
  showProperties,
  toggleShowProperties,
  selectedMeshes,
  cubeSize,
  sphereRadius,
  handleUpdateCubeSize,
  handleUpdateSphereRadius,
  handleUpdateColor,
}) => {
  const getFixtureDetails = () => {
    if (!selectedMeshes.length) {
      return <div>No fixture selected</div>;
    }

    if (selectedMeshes.length > 1) {
      return (
        <div className="fixture-properties-container">
          <div className="fixture-item-detail-wrapper">
            <div className="fixture-item-detail-property">
              Multiple Objects Selected
            </div>
            <div className="fixture-item-detail-value">
              {selectedMeshes.length} objects
            </div>
          </div>
          <div className="fixture-item-detail-wrapper">
            <div className="fixture-item-detail-property">Color</div>
            <div className="fixture-item-detail-value">
              <input
                type="color"
                value={selectedMeshes[0].userData.originalColor || '#ffffff'}
                onChange={(e) => {
                  selectedMeshes.forEach((mesh) =>
                    handleUpdateColor?.(mesh, e.target.value)
                  );
                }}
                style={{ width: '100px', height: '30px' }}
              />
            </div>
          </div>
        </div>
      );
    }

    const mesh = selectedMeshes[0];
    const isCube = mesh.userData.isCube;
    const isSphere = mesh.userData.isSphere;

    if (isCube || isSphere) {
      return (
        <div className="fixture-properties-container">
          {isCube ? (
            <>
              <div className="fixture-item-detail-wrapper">
                <div className="fixture-item-detail-property">Width</div>
                <div className="fixture-item-detail-value">
                  <Input
                    value={cubeSize.width}
                    onChange={(e) =>
                      handleUpdateCubeSize(mesh, 'width', e.target.value)
                    }
                    style={{ width: '100px' }}
                    type="number"
                    min={0.1}
                    step={0.01}
                  />
                </div>
              </div>
              <div className="fixture-item-detail-wrapper">
                <div className="fixture-item-detail-property">Height</div>
                <div className="fixture-item-detail-value">
                  <Input
                    value={cubeSize.height}
                    onChange={(e) =>
                      handleUpdateCubeSize(mesh, 'height', e.target.value)
                    }
                    style={{ width: '100px' }}
                    type="number"
                    min={0.1}
                    step={0.01}
                  />
                </div>
              </div>
              <div className="fixture-item-detail-wrapper">
                <div className="fixture-item-detail-property">Depth</div>
                <div className="fixture-item-detail-value">
                  <Input
                    value={cubeSize.depth}
                    onChange={(e) =>
                      handleUpdateCubeSize(mesh, 'depth', e.target.value)
                    }
                    style={{ width: '100px' }}
                    type="number"
                    min={0.1}
                    step={0.01}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="fixture-item-detail-wrapper">
              <div className="fixture-item-detail-property">Radius</div>
              <div className="fixture-item-detail-value">
                <Input
                  value={sphereRadius}
                  onChange={(e) =>
                    handleUpdateSphereRadius(mesh, e.target.value)
                  }
                  style={{ width: '100px' }}
                  type="number"
                  min={0.1}
                  step={0.01}
                />
              </div>
            </div>
          )}
          <div className="fixture-item-detail-wrapper">
            <div className="fixture-item-detail-property">Color</div>
            <div className="fixture-item-detail-value">
              <input
                type="color"
                value={mesh.userData.originalColor || '#ffffff'}
                onChange={(e) => handleUpdateColor?.(mesh, e.target.value)}
                style={{ width: '100px', height: '30px' }}
              />
            </div>
          </div>
        </div>
      );
    }

    const selectedFixture = mesh.userData;
    if (
      !selectedFixture ||
      !selectedFixture.type ||
      selectedFixture.type !== 'fixture'
    ) {
      return <div>No fixture selected</div>;
    }

    const fixtureId = selectedFixture.fixtureId;
    const fullFixtureData = fixtureByIdMap[fixtureId];

    return (
      <div className="fixture-properties-container">
        <div className="fixture-item-detail-wrapper">
          <div className="fixture-item-detail-property">Category</div>
          <div className="fixture-item-detail-value">
            {formatCategoryName(selectedFixture.category || 'other')}
          </div>
        </div>
        <div className="fixture-item-detail-wrapper">
          <div className="fixture-item-detail-property">Name</div>
          <div className="fixture-item-detail-value">
            {selectedFixture.name}
          </div>
        </div>
        <div className="fixture-item-detail-wrapper">
          <div className="fixture-item-detail-property">Size</div>
          <div className="fixture-item-detail-value">
            {fullFixtureData
              ? `${fullFixtureData.w}" W x ${fullFixtureData.h}" H x ${fullFixtureData.d}" D`
              : `${selectedFixture.size?.width}" W x ${selectedFixture.size?.height}" H`}
          </div>
        </div>

        {selectedFixture.imageUrl && (
          <div className="fixture-item-display-wrapper">
            <img
              src={selectedFixture.imageUrl}
              alt={selectedFixture.name}
              className="fixture-display-image"
              style={{ height: '50px', maxWidth: '100%', objectFit: 'contain' }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <section className={`properties-section ${!showProperties && 'hide'}`}>
      <div className="properties-header">
        <div className="properties-header__section-left">
          <h1>Properties</h1>
        </div>
        <div className="properties-header__section-right">
          {showProperties ? (
            <Tooltip title="Hide" mouseEnterDelay={0.5}>
              <MinusOutlined
                onClick={toggleShowProperties}
                className="properties-header-icon"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Show" mouseEnterDelay={0.5}>
              <PlusOutlined
                onClick={toggleShowProperties}
                className="properties-header-icon"
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={`properties-wrapper ${
          showProperties ? 'visible' : 'hidden'
        }`}
      >
        {selectedMeshes.length === 0 && <div>No fixture selected</div>}
        {selectedMeshes.length > 1 && <div>Multiple fixtures selected</div>}
        {selectedMeshes.length === 1 && getFixtureDetails()}
      </div>
    </section>
  );
};

export default FixtureProperties;
